'use client';
import { getEventContentTexts } from '@eliadmin/core/src/utils/project-content-text';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import Image from '@eliadmin/core/src/components/image/image-wrapped';

import { useMemo, useState } from 'react';

import type { EventDetail, LecturerInEvent } from '@eliadmin/core/src/api/events/events-api';
import type { Level, Room } from '@eliadmin/core/src/api/courses/coursers-api';

import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { buildCoursesQuery, fetchCourses } from '@eliadmin/core/src/api/courses/coursers-api';
import { useQuery } from '@tanstack/react-query';
import { CacheKey } from '@eliadmin/core/src/api/constants';

import type { ImageType } from '@eliadmin/core/src/api/api-types';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@eliadmin/core/src/components/ui/select';

import { cn } from '@eliadmin/core/src/utils/style-utils';

import { EventCourseCard } from './event-course-card';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '@eliadmin/core/src/components/ui/dialog';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';

const NO_LECTURER_SELECTED = 'none';
const NO_LEVEL_SELECTED = 'none';
const COURSES_PER_PAGE = 20;
const STUDIO_EVENT_SLUG = 'danceana-studio-nobelova';

const STUDIO_COURSES_TITLE: Record<string, string> = {
  sk: 'Vyber si tréning',
  en: 'Choose a class',
};

interface EventCoursesProps {
  event: EventDetail;
  locale: string;
  rooms: Room[];
  lecturers: LecturerInEvent[];
  levels: Level[];
  schedule: ImageType;
}

export function EventCourses({ event, locale, lecturers, levels, schedule }: EventCoursesProps) {
  const [selectedLecturer, setSelectedLecturer] = useState<string>(NO_LECTURER_SELECTED);
  const [selectedLevel, setSelectedLevel] = useState<string>(NO_LEVEL_SELECTED);

  const me = useCustomerStore(s => s.me);
  const isInitialized = useCustomerStore(s => s.isInitialized);

  const { t } = useTranslations();

  const coursesQueryParams = useMemo(
    () =>
      buildCoursesQuery({
        results: COURSES_PER_PAGE,
        events: [event.id],
        lecturers:
          selectedLecturer !== NO_LECTURER_SELECTED ? [Number(selectedLecturer)] : undefined,
        levels: selectedLevel !== NO_LEVEL_SELECTED ? [Number(selectedLevel)] : undefined,
      }),
    [selectedLecturer, selectedLevel, event.id],
  );

  const {
    data: courses,
    isLoading,
    // isError,
    // error,
  } = useQuery({
    queryKey: [CacheKey.COURSES, locale, selectedLecturer, selectedLevel, event.id],
    queryFn: async () => {
      return fetchCourses({
        locale,
        authToken: me?.authToken,
        query: {
          page: 1,
          ...coursesQueryParams,
          results: COURSES_PER_PAGE,
        },
      });
    },
    enabled: isInitialized,
  });

  const sortedCourses = useMemo(() => {
    if (!courses?.data) return [];
    if (selectedLevel !== NO_LEVEL_SELECTED) return courses.data;

    return [...courses.data].sort((a, b) => {
      const aMin = Math.min(...a.levels.map(l => l.id));
      const bMin = Math.min(...b.levels.map(l => l.id));
      return aMin - bMin;
    });
  }, [courses, selectedLevel]);

  const isStudioEvent = event.slug === STUDIO_EVENT_SLUG;
  const externalRegistrationURL = getEventContentTexts(event)?.find(field => field.id === 3)?.value;

  return (
    <div id="list" className="flex w-full flex-col items-start justify-start gap-6 sm:gap-8">
      <div className="flex w-full flex-col items-start justify-start gap-2 sm:gap-3">
        <h3 className="text-2xl font-extrabold text-black sm:text-3xl">
          {isStudioEvent
            ? (STUDIO_COURSES_TITLE[locale] ?? STUDIO_COURSES_TITLE.sk)
            : t('text2199')}
        </h3>
        {!isStudioEvent && (
          <p className="text-lg font-bold text-primary sm:text-xl">{t('text3752')}</p>
        )}
      </div>
      <div className="flex w-full flex-col items-start justify-start gap-2 sm:flex-row sm:flex-wrap">
        {/* Lecturer Selector */}
        <Select value={selectedLecturer} onValueChange={setSelectedLecturer}>
          <SelectTrigger
            className={cn('ea-event-courses__select-trigger', 'w-full sm:max-w-[275px]')}
          >
            <SelectValue className="text-xs lg:text-sm" />
          </SelectTrigger>
          <SelectContent className="max-h-[160px] overflow-y-auto rounded-none border border-black bg-white">
            <SelectItem
              value={NO_LECTURER_SELECTED}
              className="rounded-none text-sm font-medium text-black/70 transition-all hover:bg-neutral-50/50"
            >
              {t('text1948')}
            </SelectItem>
            {lecturers.map(lecturer => (
              <SelectItem
                key={lecturer.id}
                value={String(lecturer.id)}
                className="rounded-none text-sm font-medium text-black/70 transition-all hover:bg-neutral-50/50"
              >
                {lecturer.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Level Selector */}
        <Select value={selectedLevel} onValueChange={setSelectedLevel}>
          <SelectTrigger
            className={cn('ea-event-courses__select-trigger', 'w-full sm:max-w-[230px]')}
          >
            <SelectValue className="text-xs lg:text-sm" />
          </SelectTrigger>
          <SelectContent className="max-h-[160px] overflow-y-auto rounded-none border border-black bg-white">
            <SelectItem
              value={NO_LEVEL_SELECTED}
              className="rounded-none text-sm font-medium text-black/70 transition-all hover:bg-neutral-50/50"
            >
              {t('text1781')}
            </SelectItem>
            {levels.map(level => (
              <SelectItem
                key={level.id}
                value={String(level.id)}
                className="rounded-none text-sm font-medium text-black/70 transition-all hover:bg-neutral-50/50"
              >
                {level.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {!!schedule && (
          <Dialog>
            <DialogTrigger asChild>
              <button
                type="button"
                className="h-[46px] w-full bg-[#F6F6F6] px-6 text-sm font-bold text-primary sm:w-fit sm:text-base lg:h-12 xl:h-[54px]"
              >
                {t('text2202')}
              </button>
            </DialogTrigger>
            <DialogContent
              className={cn(
                'h-fit w-fit border-none p-0 sm:max-w-max',
                'ea-event-courses__dialog-content',
              )}
            >
              <DialogTitle hidden>{t('text2202')}</DialogTitle>
              <Image
                src={schedule.url}
                alt={resolveImageAlt(schedule, t('text2202'))}
                width={0}
                height={0}
                className="h-auto w-full max-w-[900px] object-cover object-center"
                sizes="100vw"
              />
            </DialogContent>
          </Dialog>
        )}
      </div>
      <div className="flex w-full flex-col items-start justify-start gap-4">
        {isLoading ? (
          <p className="w-full py-4 text-center text-sm text-black/70 sm:text-base">Loading...</p>
        ) : courses?.data.length === 0 ? (
          <p className="w-full py-4 text-center text-sm text-black/70 sm:text-base">
            {t('text2491')}
          </p>
        ) : (
          sortedCourses.map(course => (
            <EventCourseCard
              course={course}
              key={course.slug}
              externalRegistration={externalRegistrationURL}
              hidePrice={isStudioEvent}
            />
          ))
        )}
      </div>
    </div>
  );
}
